import React, { Fragment, useContext, useMemo, useRef, useEffect } from 'react';

import PresentationContext from '../PresentationContext';
import Stats from './Stats';
import TargetContext from './TargetContext';
import { exposeToClipboard, removeClipboardListener } from '../utils/clipboard';
import { mgdL } from '../utils/consts';
import { datetimeDurationFormat } from '../utils/formats';
import './Dashboard.css';
import TiRBar from './TiRBar';


function TiR({ binNames, ranges, t, title }) {
  const {
    durationFormat,
    formatGlucoseValue,
    formatGlucose,
  } = useContext(PresentationContext);
  const { targets } = useContext(TargetContext);
  const copyRef = useRef(null);

  useEffect(() => {
    let node;
    if (copyRef.current) {
      node = copyRef.current;
      exposeToClipboard(node);
    }
    return () => {
      if (node) {
        removeClipboardListener(node);
      }
    }
  }, [copyRef]);
  
  function getPosition(i, range) {
    if (i === 0) {
      return 10;
    }
    if (i === binNames.length - 1) {
      return 200;
    }
    const val = Math.min(
      (198 - ((4 - i) * 16)),
      Math.max((10 + (i * 16)), (10 + range.y + range.value / 2) * 245 / 140),
    );
    return val;
  }
  
  const displayNames = useMemo(() => ({
    hyper: (
      <Fragment>
        {t('glucoseStatsRow.tir.hyper')} <small>(&gt;{
        formatGlucose({value: 250, unit: mgdL})
        })</small>
      </Fragment>
    ),
    high: t('glucoseStatsRow.tir.high'),
    low: (
      <Fragment>
        {t('glucoseStatsRow.tir.target')} <small>({
        formatGlucoseValue({value: targets.bottom, unit: mgdL})
        }-{
          formatGlucose({value: targets.top, unit: mgdL})
        })</small>
      </Fragment>
    ),
    hypo: t('glucoseStatsRow.tir.low'),
    min: (
      <Fragment>
        {t('glucoseStatsRow.tir.hypo')} <small>(&lt;{
        formatGlucose({value: 54, unit: mgdL})
        })</small>
      </Fragment>
    ),
  }), [formatGlucose, formatGlucoseValue, t, targets]);
  
  return (
    <Stats key="overview" id="tir" className="overview">
      <h2>{t('glucoseStatsRow.tir.heading')}</h2>
      <TiRBar ranges={ranges} title={title} />
      <dl className="rangeInfo">
        {
          binNames.map((n, i) => {
            const range = ranges[n];
            return (
              <Fragment key={n}>
                <dt style={{ top: getPosition(i, range) }}>{displayNames[n]}</dt>
                <dd style={{ top: getPosition(i, range) }}>
                  <data value={range.value}>{range.value.toFixed(1)}%</data>
                  (<time dateTime={datetimeDurationFormat(range.time)}>
                    {durationFormat(range.time)}
                  </time>)
                </dd>
              </Fragment>
            );
          })
        }
      </dl>
    </Stats>
  );
}

export default TiR;
